<script setup lang="ts">
/**
 * Module: web/src/views/admin/AdminPeopleView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-10-26
 *
 * Admin > People (contract admin-people-api v1.0.0 §7, gated user_admin):
 * the staff directory -- every person row that entries, the Assisted-Entry
 * dispatcher list, quick PINs and review credits attribute to, with or
 * without a login. Owns identity (name, role labels, active, notes);
 * passwords and roles stay in User Admin, and the review-roster flag stays
 * in Review Incentives > Roster (a view over the same table).
 *
 *   - all people, active-first, with a name/email search; archived people
 *     dimmed at the bottom with Restore
 *   - per-person chips: role labels (neutral), Account (lock, the login's
 *     email on hover), PIN (key) -- so the logins/people split never has to
 *     be explained: the row simply says whether a login is attached
 *   - Add person + Edit sheets (name, role-label chips, notes); the server
 *     derives is_dispatcher from the Dispatcher label
 *   - Deactivate = soft only, with a confirm sheet that warns when a login
 *     is still active (deactivate it in User Admin) -- no hard delete exists
 *   - 409 duplicate_name surfaces as a friendly inline error
 *
 * Desktop-native, mobile-friendly (doc 12 §4.1 tier 2): single-column
 * cards, wrapped chip rows, 44px tap targets, zero page h-scroll at 375px.
 * Pattern: ReviewRosterView.vue (sheets, chips, confirm).
 */
import { computed, onMounted, ref } from 'vue';
import { apiGet, apiPatch, apiPost, unwrapList, ApiError } from '@/composables/useApi';
import type { AdminPerson, AdminPersonCreateBody, AdminPersonPatchBody, RosterRoleLabel } from '@/types/api';
import AppIcon from '@/components/AppIcon.vue';
import HelpTip from '@/components/HelpTip.vue';

/** Label vocabulary (contract §3) -- display order. */
const ROLE_LABELS: { key: RosterRoleLabel; label: string }[] = [
  { key: 'driver', label: 'Driver' },
  { key: 'dispatcher', label: 'Dispatcher' },
  { key: 'manager', label: 'Manager' },
];
const LABEL_TEXT: Record<RosterRoleLabel, string> = {
  driver: 'Driver', dispatcher: 'Dispatcher', manager: 'Manager',
};

const people = ref<AdminPerson[]>([]);
const loading = ref(false);
const loadError = ref('');
const search = ref('');

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return people.value;
  return people.value.filter((p) =>
    p.full_name.toLowerCase().includes(q)
    || (p.account_email ?? '').toLowerCase().includes(q)
    || (p.legacy_names ?? []).some((n) => n.toLowerCase().includes(q)));
});
const activePeople = computed(() => filtered.value.filter((p) => p.active));
const archivedPeople = computed(() => filtered.value.filter((p) => !p.active));

async function load() {
  loading.value = true;
  loadError.value = '';
  try {
    const body = await apiGet<unknown>('/admin/people');
    people.value = unwrapList<AdminPerson>(body, 'people');
  } catch {
    loadError.value = 'Could not load people. Check the connection and retry.';
  } finally {
    loading.value = false;
  }
}
onMounted(load);

// ---- Add / Edit sheet ---------------------------------------------------------
/** null = closed; person null = Add mode; person set = Edit mode. */
const sheet = ref<{ person: AdminPerson | null } | null>(null);
const fName = ref('');
const fLabels = ref<RosterRoleLabel[]>([]);
const fNotes = ref('');
const saving = ref(false);
const sheetError = ref('');

function openAdd() {
  sheet.value = { person: null };
  fName.value = '';
  fLabels.value = [];
  fNotes.value = '';
  sheetError.value = '';
}

function openEdit(p: AdminPerson) {
  sheet.value = { person: p };
  fName.value = p.full_name;
  fLabels.value = [...(p.role_labels ?? [])];
  fNotes.value = p.notes ?? '';
  sheetError.value = '';
}

function toggleLabel(key: RosterRoleLabel) {
  fLabels.value = fLabels.value.includes(key)
    ? fLabels.value.filter((k) => k !== key)
    : [...fLabels.value, key];
}

function sheetErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 409) return 'An active person already has this name — rename or restore the existing entry instead.';
    if (e.status === 422) return 'The server rejected that change — check the name and try again.';
  }
  return 'Could not save. Check the connection and try again.';
}

async function saveSheet() {
  if (!sheet.value || !fName.value.trim()) return;
  saving.value = true;
  sheetError.value = '';
  try {
    if (sheet.value.person === null) {
      const body: AdminPersonCreateBody = {
        full_name: fName.value.trim(),
        role_labels: [...fLabels.value],
      };
      if (fNotes.value.trim()) body.notes = fNotes.value.trim();
      await apiPost('/admin/people', body);
    } else {
      const body: AdminPersonPatchBody = {
        full_name: fName.value.trim(),
        role_labels: [...fLabels.value],
        notes: fNotes.value.trim() ? fNotes.value.trim() : null,
      };
      await apiPatch(`/admin/people/${sheet.value.person.id}`, body);
    }
    sheet.value = null;
    await load();
  } catch (e) {
    sheetError.value = sheetErrorMessage(e);
  } finally {
    saving.value = false;
  }
}

// ---- deactivate / restore -----------------------------------------------------
const confirmTarget = ref<AdminPerson | null>(null);
const acting = ref(false);
const actionError = ref('');

async function setActive(p: AdminPerson, active: boolean) {
  acting.value = true;
  actionError.value = '';
  try {
    await apiPatch(`/admin/people/${p.id}`, { active });
    confirmTarget.value = null;
    await load();
  } catch {
    actionError.value = `Could not ${active ? 'restore' : 'deactivate'} ${p.full_name}. Try again.`;
  } finally {
    acting.value = false;
  }
}
</script>

<template>
  <div class="px-4 py-5 md:p-6 w-full max-w-4xl">
    <!-- Header -->
    <div class="flex items-start justify-between gap-3 mb-3">
      <div class="min-w-0">
        <h1 class="text-xl font-bold text-fg flex items-center gap-2">
          <AppIcon name="user" class="w-6 h-6 text-accent" aria-hidden="true" />
          People
        </h1>
        <p class="text-sm text-fg-muted">
          Everyone who works here, with or without a login. Entries, the entry queue, PINs and review credits
          attribute to these rows.
          <HelpTip k="admin.people" />
        </p>
      </div>
      <button class="btn-primary !h-10 !px-3 text-sm flex-shrink-0" @click="openAdd">
        Add person
      </button>
    </div>

    <div class="mb-4">
      <label class="sr-only" for="people-search">Search people</label>
      <input
        id="people-search"
        v-model="search"
        class="field-input"
        type="search"
        placeholder="Search by name or login email"
        autocomplete="off"
      />
    </div>

    <div v-if="loadError" class="alert-error mb-4" role="alert">{{ loadError }}</div>
    <div v-if="actionError" class="alert-error mb-4" role="alert">{{ actionError }}</div>

    <div
      v-if="loading && people.length === 0"
      class="space-y-2.5"
      aria-label="Loading people"
    >
      <div v-for="i in 3" :key="`sk-${i}`" class="bg-card border border-border-c rounded-lg p-4 shadow-card">
        <div class="skeleton h-4 w-1/2 mb-3" />
        <div class="skeleton h-9 w-44" />
      </div>
    </div>

    <template v-else>
      <!-- Active people -->
      <div class="space-y-2.5">
        <div
          v-for="p in activePeople"
          :key="p.id"
          class="bg-card border border-border-c rounded-lg shadow-card p-4 min-w-0"
        >
          <div class="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span class="font-semibold text-fg truncate">{{ p.full_name }}</span>
            <span
              v-for="lbl in p.role_labels"
              :key="lbl"
              class="chip chip-neutral"
            >{{ LABEL_TEXT[lbl] }}</span>
            <span
              v-if="p.has_account"
              class="chip chip-neutral"
              :title="p.account_email ? `Login: ${p.account_email}` : 'Has a login'"
            ><AppIcon name="lock" class="w-3 h-3" /> Account</span>
            <span
              v-if="p.has_active_pin"
              class="chip chip-neutral"
              title="Has an active quick PIN"
            ><AppIcon name="key" class="w-3 h-3" /> PIN</span>
            <span v-if="!p.has_account" class="ml-auto text-xs text-fg-muted whitespace-nowrap">no login</span>
          </div>
          <p v-if="p.notes" class="mt-1.5 text-xs text-fg-muted">{{ p.notes }}</p>
          <div class="mt-2.5 flex items-center gap-2.5 flex-wrap">
            <button type="button" class="btn-secondary !h-9 !px-3 text-sm" :disabled="acting" @click="openEdit(p)">
              Edit
            </button>
            <button
              type="button"
              class="btn-secondary !h-9 !px-3 text-sm"
              :disabled="acting"
              @click="confirmTarget = p"
            >
              Deactivate
            </button>
          </div>
        </div>
        <div v-if="activePeople.length === 0" class="empty-state">
          <div class="empty-state-icon"><AppIcon name="users" class="w-5 h-5" /></div>
          <p class="empty-state-title">{{ search.trim() ? 'No one matches that search' : 'No people yet' }}</p>
          <p class="empty-state-hint">
            {{ search.trim() ? 'Try another name, or check the archived list below.' : 'Add your first person with the button above.' }}
          </p>
        </div>
      </div>

      <!-- Archived -->
      <template v-if="archivedPeople.length">
        <h2 class="text-sm font-semibold text-fg-muted mt-6 mb-2">
          Archived ({{ archivedPeople.length }})
        </h2>
        <div class="space-y-2.5">
          <div
            v-for="p in archivedPeople"
            :key="p.id"
            class="bg-card border border-border-c rounded-lg p-4 min-w-0 opacity-70"
          >
            <div class="flex items-center gap-x-2 gap-y-1 flex-wrap">
              <span class="font-semibold text-fg truncate">{{ p.full_name }}</span>
              <span class="chip chip-neutral">Archived</span>
              <span
                v-for="lbl in p.role_labels"
                :key="lbl"
                class="chip chip-neutral"
              >{{ LABEL_TEXT[lbl] }}</span>
              <span
                v-if="p.has_account"
                class="chip chip-warm"
                :title="p.account_email ? `Login still active: ${p.account_email}` : 'Login still active'"
              ><AppIcon name="lock" class="w-3 h-3" /> Login still active</span>
            </div>
            <p v-if="p.notes" class="mt-1.5 text-xs text-fg-muted">{{ p.notes }}</p>
            <div class="mt-2.5">
              <button
                type="button"
                class="btn-secondary !h-9 !px-3 text-sm"
                :disabled="acting"
                @click="setActive(p, true)"
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      </template>

      <p class="text-[11px] text-fg-muted mt-5">
        Deactivating keeps every entry, PIN and review credit — it only removes the person from the
        pickers. Logins are separate: deactivate those in User Admin. There is no delete.
      </p>
    </template>

    <!-- ===== Add / Edit sheet ===== -->
    <div v-if="sheet" class="fixed inset-0 z-40">
      <div class="absolute inset-0 bg-black/60" @click="saving ? null : (sheet = null)" />
      <div
        class="absolute bottom-0 inset-x-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:w-[26rem] bg-card border border-border-c rounded-t-2xl sm:rounded-2xl shadow-2xl p-5"
        role="dialog"
        aria-modal="true"
        :aria-label="sheet.person ? 'Edit person' : 'Add person'"
      >
        <h3 class="font-bold text-fg mb-1">{{ sheet.person ? 'Edit person' : 'Add person' }}</h3>
        <p class="text-sm text-fg-muted mb-4">
          {{ sheet.person
            ? 'Identity only — passwords and roles are managed in User Admin.'
            : 'Someone without a login (or whose login comes later). Creating a user adds its person here automatically.' }}
        </p>
        <div class="space-y-3 mb-4">
          <div>
            <label class="field-label" for="people-name">Full name</label>
            <input
              id="people-name"
              v-model="fName"
              class="field-input"
              type="text"
              maxlength="120"
              placeholder="e.g. Jane Smith"
            />
          </div>
          <div>
            <div class="field-label !mb-2">
              Role labels (any that apply) <HelpTip k="admin.people.role_labels" />
            </div>
            <div class="flex items-center gap-2 flex-wrap" role="group" aria-label="Role labels">
              <button
                v-for="rl in ROLE_LABELS"
                :key="rl.key"
                type="button"
                :aria-pressed="fLabels.includes(rl.key)"
                class="h-11 px-3.5 rounded-full border text-sm font-medium transition-colors"
                :class="fLabels.includes(rl.key)
                  ? 'bg-accent border-accent text-white'
                  : 'bg-input border-border-c text-fg hover:bg-card'"
                @click="toggleLabel(rl.key)"
              >{{ rl.label }}</button>
            </div>
          </div>
          <div>
            <label class="field-label" for="people-notes">
              Notes <span class="text-fg-muted font-normal">(optional)</span>
            </label>
            <textarea
              id="people-notes"
              v-model="fNotes"
              class="field-input !h-20"
              maxlength="500"
              placeholder="e.g. Nights only; started July 2026"
            />
          </div>
        </div>
        <div v-if="sheetError" class="alert-error mb-3" role="alert">{{ sheetError }}</div>
        <div class="flex gap-2.5">
          <button type="button" class="btn-secondary flex-1" :disabled="saving" @click="sheet = null">
            Cancel
          </button>
          <button
            type="button"
            class="btn-primary flex-[1.6]"
            :disabled="saving || !fName.trim()"
            @click="saveSheet"
          >
            {{ saving ? 'Saving…' : sheet.person ? 'Save changes' : 'Add person' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== Deactivate confirm sheet ===== -->
    <div v-if="confirmTarget" class="fixed inset-0 z-40">
      <div class="absolute inset-0 bg-black/60" @click="acting ? null : (confirmTarget = null)" />
      <div
        class="absolute bottom-0 inset-x-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:w-[26rem] bg-card border border-border-c rounded-t-2xl sm:rounded-2xl shadow-2xl p-5"
        role="dialog"
        aria-modal="true"
        aria-label="Deactivate person"
      >
        <h3 class="font-bold text-fg mb-1">Deactivate {{ confirmTarget.full_name }}?</h3>
        <p class="text-sm text-fg-muted mb-2">
          They drop out of the dispatcher list, the PIN picker and new review suggestions. Every entry,
          PIN and review credit they already have stays. You can restore them any time.
        </p>
        <p v-if="confirmTarget.has_account" class="text-sm text-accent-warm mb-4">
          Their login<span v-if="confirmTarget.account_email"> ({{ confirmTarget.account_email }})</span> stays
          active — if they have left, deactivate it in User Admin too.
        </p>
        <p v-else class="mb-4"></p>
        <div class="flex gap-2.5">
          <button type="button" class="btn-secondary flex-1" :disabled="acting" @click="confirmTarget = null">
            Cancel
          </button>
          <button
            type="button"
            class="btn-primary flex-[1.6]"
            :disabled="acting"
            @click="setActive(confirmTarget, false)"
          >
            {{ acting ? 'Working…' : 'Deactivate' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
